import { ChevronLeft, MenuOpenRounded, MonetizationOn, MoneyRounded, PointOfSaleTwoTone, SupportAgent, SwapHoriz } from '@mui/icons-material';
import MoreVert from '@mui/icons-material/MoreVert';
import PeopleIcon from '@mui/icons-material/People';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PercentIcon from '@mui/icons-material/Percent';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Avatar, Box, Button, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ConfirmationModal from '../../../components/ConfirmationModal';
import Dropdown from '../../../components/Dropdown';
import { LoadingContent } from '../../../components/Loading';
import useErrors from '../../../hooks/useErrors';
import { useFetch } from '../../../hooks/useFecth';
import { useAuth } from '../../../context/AuthContext';
import { pickSessionUser, stageSupportHandoff } from '../../../services/authSession';
import server from '../../../services/server';
import BannersClub from './components/BannersClub';
import DialogClubs from './components/DialogClubs';
import ListRescues from './components/ListRescues';
import ListSubscribers from './components/ListSubscribers';
import MenuClub from './components/MenuClub';
import ProductsClub from './components/ProductsClub';
import SettingsClub from './components/SettingsClub';
import ContractClub from './components/ContractClub';
import ListClubTeam from './components/ListClubTeam';
import ResellerClub from './components/ResellerClub';
import ClubSubscriberMigration from './components/ClubSubscriberMigration';
import ListImports from '../../../components/ListImports';

const DOMAIN_STATUS_LABELS = {
    processing: 'Verificando DNS',
    provisioning_hostname: 'Emitindo certificado',
    provisioning_route: 'Conectando ao clube',
    verifying: 'Finalizando ativação',
    active: 'Ativo',
    removing: 'Removendo domínio',
    failed_cname: 'CNAME ausente ou incorreto',
    failed: 'Não foi possível concluir',
    pending: 'Pendente',
    pending_validation: 'Em validação',
    blocked: 'Bloqueado',
    suspended: 'Suspenso',
    deleted: 'Removido',
    error: 'Erro',
};

const DOMAIN_STATUS_ERROR = new Set(['failed', 'failed_cname', 'error', 'blocked', 'suspended', 'deleted']);

function resolveDomainStatus(domain, domainStatus) {
    if (!domain) return { label: 'Não configurado', color: 'default' };
    const key =
        typeof domainStatus === 'string' ? domainStatus : domainStatus?.result?.status || domainStatus?.status || '';
    return {
        label: DOMAIN_STATUS_LABELS[key] || key || '—',
        color: key === 'active' ? 'success' : DOMAIN_STATUS_ERROR.has(key) ? 'error' : key ? 'warning' : 'default',
    };
}

function MetaField({ label, children }) {
    return (
        <Box minWidth={0}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontWeight: 700, letterSpacing: 0.2 }}
            >
                {label}
            </Typography>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" fontWeight={700} noWrap title={String(children)}>
                    {children}
                </Typography>
            ) : (
                children
            )}
        </Box>
    );
}

export default function ClubsDetails() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('tab') === 'migracao' ? '#tab-10' : '#tab-9');
    const navigate = useNavigate();
    const { user } = useAuth();
    const { data, error, mutate, isFetching } = useFetch(`clubs/${id}`);
    const { setErrors, enqueueSnackbar } = useErrors();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [supportOpen, setSupportOpen] = useState(false);
    const [supportCode, setSupportCode] = useState('');
    const [supportLoading, setSupportLoading] = useState(false);

    const view = searchParams.get('view');

    useEffect(() => {
        if (searchParams.get('tab') === 'migracao') setTab('#tab-10');
    }, [searchParams]);

    const order = data?.Contract?.Order?.find(i => i);

    if (isFetching) return <LoadingContent />;
    if (error) return <>Error: {error?.message}</>;
    if (!data) return <>Tente novamente mais tarde</>;

    const handleDelete = () => {
        setConfirmAction(() => async () => {
            try {
                await server.delete(`clubs/${id}`);
                enqueueSnackbar('Clube e todos os dados relacionados foram excluídos permanentemente.');
                navigate('/dashboard/clubes'); // Redireciona para a lista de clubes
            } catch (error) {
                setErrors('Erro ao deletar clube.', error);
            }
            setConfirmOpen(false);
        });
        setConfirmOpen(true);
    };

    const handleSupportEnter = async () => {
        const code = String(supportCode || '').trim();
        if (!/^\d{6}$/.test(code)) {
            enqueueSnackbar('Informe o código de 6 dígitos gerado pelo clube.', { variant: 'warning' });
            return;
        }

        const supportTab = window.open('about:blank', '_blank');
        if (!supportTab) {
            enqueueSnackbar('Permita pop-ups para abrir o clube em nova guia.', { variant: 'warning' });
            return;
        }

        try {
            setSupportLoading(true);
            const { data: response } = await server.post(`clubs/${id}/support/enter`, { code });
            const payload = response?.data || response;
            const nonce = stageSupportHandoff({
                token: payload.token,
                refreshtoken: payload.refreshToken,
                user: pickSessionUser(payload.user),
                club: payload.club,
            });

            if (!nonce) {
                supportTab.close();
                enqueueSnackbar('Não foi possível preparar a sessão de suporte.', { variant: 'error' });
                setSupportLoading(false);
                return;
            }

            supportTab.location.href = `${window.location.origin}/clube?supportHandoff=${encodeURIComponent(nonce)}`;
            setSupportOpen(false);
            setSupportCode('');
            enqueueSnackbar('Painel do clube aberto em nova guia.');
        } catch (error) {
            supportTab.close();
            setErrors(error);
        } finally {
            setSupportLoading(false);
        }
    };

    const contactPhone = data?.responsiblePhone || data?.supportPhone || data?.phone || '';
    const contactPhoneFormatted = contactPhone
        ? contactPhone
              .replace(/\D/g, '')
              .replace(/(\d{2})(\d)/, '($1) $2')
              .replace(/(\d{5})(\d)/, '$1-$2')
              .replace(/(-\d{4})\d+?$/, '$1')
        : '';

    const domainStatus = resolveDomainStatus(data?.domain, data?.domainStatus);
    const planLabel = order?.Order_products?.map(({ Product }) => Product?.name).join(', ') || '--';
    const responsibleLabel = data?.responsibleName || data?.Contract?.contractData?.contract?.contractorName || '--';
    const categoryCount = data?.ClubCategory?.length || 0;

    return (
        <Container maxWidth="xl" sx={{ gap: 3, flexDirection: 'column', display: 'flex' }}>
            <Stack alignItems="center" direction="row" columnGap={1} pb={2}>
                <IconButton component={Link} to={-1} replace={true}>
                    <ChevronLeft />
                </IconButton>
                <Typography variant="h2">Detalhes do clube</Typography>
            </Stack>

            <Card>
                <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, '&:last-child': { pb: 2.5 } }}>
                    <Stack direction="row" alignItems="flex-start" gap={2}>
                        <Avatar
                            variant="rounded"
                            src={data.logo}
                            alt={data?.name}
                            sx={{
                                width: 112,
                                height: 72,
                                borderRadius: 2,
                                bgcolor: data?.primaryColor || 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& img': { objectFit: 'contain' },
                            }}
                        />
                        <Box flex={1} minWidth={0}>
                            <Typography variant="h4">{data?.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                #{data?.id}
                                {data.businessName ? ` · ${data.businessName}` : ''}
                                {categoryCount > 0 ? ` · ${categoryCount} categorias` : ''}
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1, minWidth: 0 }}>
                                <Box
                                    title={data.status ? 'Clube ativo' : 'Clube inativo'}
                                    aria-label={data.status ? 'Clube ativo' : 'Clube inativo'}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        bgcolor: data.status ? 'success.main' : 'text.disabled',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    noWrap
                                    title={data.domain ? `${data.domain} · ${domainStatus.label}` : domainStatus.label}
                                >
                                    {data.domain || 'Sem domínio personalizado'}
                                </Typography>
                            </Stack>
                        </Box>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SupportAgent />}
                                onClick={() => {
                                    setSupportCode('');
                                    setSupportOpen(true);
                                }}
                            >
                                Suporte
                            </Button>
                            <Dropdown>
                                <IconButton>
                                    <MoreVert />
                                </IconButton>
                                <Menu placement="bottom-start">
                                    <MenuItem component={Link} to={`#clubs|${data.id}`}>
                                        Alterar dados
                                    </MenuItem>
                                    {Boolean(user?.super) && (
                                        <MenuItem color="danger" onClick={handleDelete}>
                                            Excluir
                                        </MenuItem>
                                    )}
                                    <Divider />
                                    <MenuItem color="danger">Mig ID: {data.originId}</MenuItem>
                                    <MenuItem
                                        color="danger"
                                        onClick={() => {
                                            navigator.clipboard.writeText(data.accessCode);
                                            enqueueSnackbar('ID da migração copiado para a área de transferência');
                                        }}
                                    >
                                        AC: {data.accessCode}
                                    </MenuItem>
                                </Menu>
                            </Dropdown>
                        </Stack>
                    </Stack>

                    <Divider />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 2,
                        }}
                    >
                        <MetaField label="Plano">{planLabel}</MetaField>
                        <MetaField label="Responsável">{responsibleLabel}</MetaField>
                        <MetaField label="Contato">{contactPhoneFormatted || '--'}</MetaField>
                    </Box>
                </CardContent>
            </Card>

            <TabContext value={tab}>
                <Stack spacing={2}>
                <Card>
                    <TabList
                        onChange={(e, next) => setTab(next)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            px: 1,
                            minHeight: 56,
                            '& .MuiTab-root': { minHeight: 56 },
                        }}
                    >
                        {/* <Tab icon={<PeopleIcon />} iconPosition="start" value="#tab-1" label="Assinantes" /> */}
                        <Tab icon={<PeopleAltRounded />} iconPosition="start" value="#tab-9" label="Equipe" />
                        {/* {data?.Contract?.contractData?.functions?.includes('reseller') && (
                            <Tab icon={<StorefrontIcon />} iconPosition="start" value="#tab-2" label="Planos" />
                        )} */}
                        {/* <Tab icon={<PercentIcon />} iconPosition="start" value="#tab-3" label="Resgates" /> */}
                        {/* <Tab icon={<ViewCarouselIcon />} iconPosition="start" value="#tab-4" label="Banners" /> */}
                        {/* <Tab icon={<MenuOpenRounded />} value="#tab-5" iconPosition="start" label="Menu" /> */}
                        <Tab icon={<SettingsApplicationsIcon />} value="#tab-6" iconPosition="start" label="Configurações" />
                        <Tab icon={<MoneyRounded />} value="#tab-7" iconPosition="start" label="Contrato" />
                        {(data?.Contract?.contractData?.functions?.includes('reseller') ||
                            data?.Contract?.contractData?.functions?.includes('benefit') ||
                            Boolean(data?.Contract?.contractData?.additionals?.length)) && (
                            <Tab icon={<PointOfSaleTwoTone />} value="#tab-8" iconPosition="start" label="Revenda" />
                        )}
                        {Boolean(data?.originId) && (
                            <Tab icon={<SwapHoriz />} iconPosition="start" value="#tab-10" label="Migração" />
                        )}
                    </TabList>
                </Card>
                {/* <TabPanel value="#tab-1" sx={{ p: 0 }}>
                    {view === 'imports' ? (
                        <ListImports
                            clubData={data}
                            onBack={() => {
                                const next = new URLSearchParams(searchParams);
                                next.delete('view');
                                setSearchParams(next);
                            }}
                        />
                    ) : (
                        <ListSubscribers clubId={id} club={data} />
                    )}
                </TabPanel> */}
                <TabPanel value="#tab-9" sx={{ p: 0 }}>
                    <ListClubTeam data={data} mutate={mutate} />
                </TabPanel>
                {/* <TabPanel value="#tab-2" sx={{ p: 0 }}>
                    <ProductsClub club={data} />
                </TabPanel> */}
                <TabPanel value="#tab-3" sx={{ p: 0 }}>
                    <ListRescues />
                </TabPanel>
                {/* <TabPanel value="#tab-4" sx={{ p: 0 }}>
                    <BannersClub />
                </TabPanel>
                <TabPanel value="#tab-5" sx={{ p: 0 }}>
                    <MenuClub />
                </TabPanel> */}
                <TabPanel value="#tab-6" sx={{ p: 0 }}>
                    <SettingsClub data={data} mutate={mutate} />
                </TabPanel>
                <TabPanel value="#tab-7" sx={{ p: 0 }}>
                    <ContractClub data={data} mutate={mutate} />
                </TabPanel>
                <TabPanel value="#tab-8" sx={{ p: 0 }}>
                    <ResellerClub data={data} mutate={mutate} />
                </TabPanel>
                {Boolean(data?.originId) && (
                    <TabPanel value="#tab-10" sx={{ p: 0 }}>
                        <ClubSubscriberMigration club={data} />
                    </TabPanel>
                )}
                </Stack>
            </TabContext>

            <ConfirmationModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmAction}
                title="Excluir clube permanentemente"
                message={`Tem certeza de que deseja excluir permanentemente o clube "${data?.name || id}"? Esta ação apaga assinantes, contratos, pedidos, planos, equipe e demais dados do clube e não pode ser desfeita.`}
            />

            <Dialog open={supportOpen} onClose={() => !supportLoading && setSupportOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Entrar como suporte</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Peça ao gestor do clube o código temporário de 6 dígitos (menu do usuário → Solicitar suporte).
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Código de suporte"
                        placeholder="000000"
                        value={supportCode}
                        onChange={e => setSupportCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.35em', fontFamily: 'monospace' } }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSupportEnter();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSupportOpen(false)} disabled={supportLoading}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleSupportEnter} loading={supportLoading} disabled={supportCode.length !== 6}>
                        Entrar no clube
                    </Button>
                </DialogActions>
            </Dialog>

            <DialogClubs mutate={mutate} />
        </Container>
    );
}
